import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

declare global {
  interface Window {
    DATADOG_RUM_ENABLED: boolean;
  }
}

/**
 * Log information to Datadog
 */
export const logInfo = (
  message: string,
  context: Record<string, unknown> = {}
) => {
  if (window.DATADOG_RUM_ENABLED) {
    datadogLogs.logger.info(message, context);
  }
};

/**
 * Log warning to Datadog
 */
export const logWarning = (
  message: string,
  context: Record<string, unknown> = {}
) => {
  if (window.DATADOG_RUM_ENABLED) {
    datadogLogs.logger.warn(message, context);
  }
};

/**
 * Log error to both Datadog RUM and Logs
 */
export const logError = ({
  message,
  context = {},
  error,
}: {
  message: string;
  error: Error | string;
  context: Record<string, unknown>;
}) => {
  if (!window.DATADOG_RUM_ENABLED) return;

  const newError =
    error instanceof Error ? error : new Error(`${message}. ${error}`);

  // Log to Datadog RUM
  datadogRum.addError(error, {
    source: 'custom',
    errorMessage: message,
    ...context,
  });

  // Log to Datadog Logs
  datadogLogs.logger.error(message, context, newError);
};

/**
 * Track user action in Datadog RUM
 */

export interface DatadogUserInfo {
  id: string;
  email?: string;
  name?: string;
  accountId?: string;
  accountName?: string;
  roleId?: number | string;
  [key: string]: unknown; // For any additional properties you might add in the future
}

/**
 * Partially masks email address (e.g., "john@example.com" -> "jo***@example.com")
 */
const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!domain) return '***';

  const visibleLocal = localPart.slice(0, 2);

  return `${visibleLocal}***@${domain}`;
};

/**
 * Partially masks name (e.g., "John Doe" -> "Joh***")
 */
const maskName = (name: string): string => {
  if (name.length <= 3) return '***';
  return `${name.slice(0, 3)}***`;
};

export const setUserInfo = (user: DatadogUserInfo) => {
  if (!window.DATADOG_RUM_ENABLED) return;

  // Mask any PII information before sending to Datadog
  const maskedUser: DatadogUserInfo = {
    ...user,
    ...(user.email && { email: maskEmail(user.email) }),
    ...(user.name && { name: maskName(user.name) }),
  };

  datadogRum.setUser(maskedUser);
  datadogLogs.setUser(maskedUser);
};

export const clearUser = (): void => {
  if (window.DATADOG_RUM_ENABLED) {
    datadogRum.clearUser();

    // If using Logs, do the same there
    if (datadogLogs) {
      datadogLogs.clearUser();
    }
  }
};
